import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FlexRow } from 'ui-components/components/flex/row';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Pill } from 'ui-components/components/pill';
import { SimpleDropdown } from 'ui-components/components/dropdown';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';

import type { WorkflowStep } from '../../types.d';
import { TriggerRegistry } from '../../registries/trigger-registry';
import { messages } from './messages';
import styles from './styles.module.less';

type TriggerEditorProps = {
  step: WorkflowStep;
  onUpdateStep: (updatedStep: WorkflowStep) => void;
  onEntityTypeChange: (entityType: 'Order') => void;
};

export const TriggerEditor: FC<TriggerEditorProps> = ({ step, onUpdateStep, onEntityTypeChange }) => {
  const intl = useIntl();

  const triggerOptions = useMemo(
    () => TriggerRegistry.map((entry) => ({ value: entry.eventType, label: intl.formatMessage(entry.labelMessage) })),
    [intl],
  );

  const selectedEventType = step.eventType ?? '';
  const selectedEntry = TriggerRegistry.find((e) => e.eventType === selectedEventType);

  const EventFilterInput = selectedEntry?.eventFilterInput || null;
  const handleTriggerChange = useCallback(
    (eventType: string) => {
      const entry = TriggerRegistry.find((e) => e.eventType === eventType) ?? TriggerRegistry[0];
      onUpdateStep({
        ...step,
        title: intl.formatMessage(entry.labelMessage),
        eventType: entry.eventType,
        isConfigured: true,
        eventFilters: [],
      });
      onEntityTypeChange('Order');
    },
    [step, intl, onUpdateStep, onEntityTypeChange],
  );

  return (
    <FlexColumn gap={Gap.Space12}>
      <FlexRow gap={Gap.Space8}>
        <Pill className={styles['when-pill']} label={intl.formatMessage(messages.triggerLabel)} theme='no-status' />
        <SimpleDropdown
          label={''}
          onItemSelected={handleTriggerChange}
          options={triggerOptions}
          placeholder={intl.formatMessage(messages.triggerPlaceholder)}
          selectedItemValue={selectedEventType}
        />
      </FlexRow>
      {selectedEntry?.hasEventFilter && EventFilterInput && (
        <FlexRow gap={Gap.Space8}>
          <Text textType={TextType.Label} value={intl.formatMessage(messages.triggerFilterLabel)} />
          <EventFilterInput onUpdateStep={onUpdateStep} step={step} />
        </FlexRow>
      )}
    </FlexColumn>
  );
};
